import React, { useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography, Collapse, IconButton, Button, Chip, CircularProgress } from '@mui/material';
import { ExpandMore as ExpandIcon, ExpandLess as CollapseIcon } from '@mui/icons-material';

/**
 * Q-03 (Tim, Aug 25): "dropdown(s) for each category of documents in the library
 * (Case Studies, Causal Analyses, Audits, etc.) with check boxes for those desired for
 * the benchmark report … and a 'select all' button for each category."
 *
 * One collapsible row per category; select-all per category; the count of picked
 * documents is shown up top with a Clear link.
 */
export default function BenchmarkDocumentPicker({ groups, selected, onChange, loading }) {
  const [open, setOpen] = useState({});
  const sel = new Set(selected || []);
  const toggleDoc = (id) => { const n = new Set(sel); if (n.has(id)) n.delete(id); else n.add(id); onChange([...n]); };
  const toggleGroup = (g) => {
    const ids = g.items.map((c) => c.id);
    const all = ids.every((id) => sel.has(id));
    const n = new Set(sel);
    ids.forEach((id) => (all ? n.delete(id) : n.add(id)));
    onChange([...n]);
  };

  if (loading) return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}><CircularProgress size={14} /><Typography variant="caption" color="text.secondary">Loading the library…</Typography></Box>;
  if (!groups.length) return <Typography variant="caption" color="text.secondary">No analyzed documents in the library yet — upload one in the Document Upload Portal.</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {sel.size ? <><b>{sel.size}</b> document{sel.size === 1 ? '' : 's'} picked</> : 'Pick one or more documents, or leave empty to use the filters below'}
        </Typography>
        {sel.size > 0 && <Button size="small" onClick={() => onChange([])} sx={{ minWidth: 0, textTransform: 'none' }}>Clear</Button>}
      </Box>
      {groups.map((g) => {
        const ids = g.items.map((c) => c.id);
        const n = ids.filter((id) => sel.has(id)).length;
        const all = n === ids.length; const some = n > 0 && !all;
        const isOpen = Boolean(open[g.key]);
        return (
          <Box key={g.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 0.75, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', pr: 0.5 }}>
              <FormControlLabel
                sx={{ flex: 1, m: 0, pl: 0.5 }}
                control={<Checkbox size="small" checked={all} indeterminate={some} onChange={() => toggleGroup(g)} inputProps={{ 'aria-label': `Select all ${g.label}` }} />}
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Typography variant="body2" fontWeight={600}>{g.label}</Typography><Chip size="small" label={n ? `${n} of ${ids.length}` : ids.length} sx={{ height: 18, fontSize: 11 }} /></Box>}
              />
              <IconButton size="small" onClick={() => setOpen((o) => ({ ...o, [g.key]: !isOpen }))} aria-label={isOpen ? `Collapse ${g.label}` : `Expand ${g.label}`}>
                {isOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
              </IconButton>
            </Box>
            <Collapse in={isOpen} unmountOnExit>
              <Box sx={{ pl: 3.5, pr: 1, pb: 0.5 }}>
                {g.items.map((c) => (
                  <FormControlLabel
                    key={c.id}
                    sx={{ display: 'flex', m: 0, alignItems: 'flex-start' }}
                    control={<Checkbox size="small" checked={sel.has(c.id)} onChange={() => toggleDoc(c.id)} sx={{ py: 0.25 }} />}
                    label={<Box sx={{ py: 0.25 }}><Typography variant="body2">{c.title}</Typography><Typography variant="caption" color="text.secondary">{[c.industry, c.organizationId ? 'our organization' : 'industry library'].filter(Boolean).join(' • ')}</Typography></Box>}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}
